import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ArrowRight, Ban, History, Info, Send } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { useActiveOrg } from '@/stores/auth';
import { usePermissions } from '@/hooks/usePermissions';
import { formatDate, formatDateTime } from '@/lib/dates';
import { useToday } from '@/hooks/useOrgSettings';
import { PageHeader } from '@/app/DashboardLayout';
import { canConvert, type ProformaStatus } from '@fint/validation';
import {
  Badge, Button, Card, CardContent, ErrorState, Input, Money, ProformaStatusBadge, SkeletonTable,
} from '@/features/common/ui';
import { Attachments } from '@/features/common/Attachments';
import { DocumentSection, documentQueryKey } from '@/features/documents/DocumentSection';

interface ProformaDetail {
  id: string;
  number: string;
  status: ProformaStatus;
  date: string;
  expiryDate: string | null;
  currency: string;
  total: string;
  gstSplitKind: string | null;
  isTaxInvoice: false;
  convertedToInvoiceId: string | null;
  convertedToInvoiceNumber: string | null;
  customer: { id: string; displayName: string; email: string | null; gstin: string | null } | null;
  revision: {
    /** What the customer is holding. Null until the first send. */
    latest: number | null;
    latestSentAt: string | null;
    latestReason: 'sent' | 'converted' | null;
    latestTotal: string | null;
    /** Edited since that send: the screen and the customer's copy disagree. */
    hasUnsentChanges: boolean;
  };
}

interface ProformaRevision {
  id: string;
  revision: number;
  reason: 'sent' | 'converted';
  total: string;
  currency: string;
  sentAt: string;
  sentByName: string | null;
  lineCount: number;
  /** The one they were holding when they agreed. */
  isFinal: boolean;
  /** Null on the oldest: nothing behind it to compare against. */
  changes: string[] | null;
}

/**
 * One proforma, as it stands.
 *
 * ── WHY THE BANNER IS NOT OPTIONAL ──
 * The document below the record - the same renderer the invoice page uses,
 * fed by the server's document view - is titled PROFORMA INVOICE and owes
 * nothing, and it is what gets printed, downloaded and emailed. The banner
 * is still here for the person LOOKING at the page: a screen that lists a
 * customer, a GSTIN, line items and a GST breakdown will be read as a tax
 * invoice unless it says otherwise where the eye lands first, and that is
 * precisely the mistake this feature exists to prevent.
 */
export function ProformaDetailPage() {
  const { proformaId } = useParams<{ proformaId: string }>();
  const org = useActiveOrg();
  const perms = usePermissions();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const today = useToday();
  // Null until chosen, so the default follows the org's today rather than
  // freezing the browser's timezone at first render. See useToday.
  const [pickedInvoiceDate, setPickedInvoiceDate] = useState<string | null>(null);
  const invoiceDate = pickedInvoiceDate ?? today;

  const query = useQuery({
    queryKey: ['proforma', org?.id, proformaId],
    queryFn: () => api.get<ProformaDetail>(`/proformas/${proformaId}`),
    enabled: !!org?.id && !!proformaId,
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['proforma', org?.id, proformaId] });
    // A send or a conversion changes what the document says of itself.
    if (proformaId) void queryClient.invalidateQueries({ queryKey: documentQueryKey('proformas', org?.id, proformaId) });
    void queryClient.invalidateQueries({ queryKey: ['proformas', org?.id] });
  };

  const send = useMutation({
    mutationFn: () => api.post(`/proformas/${proformaId}/send`),
    onSuccess: refresh,
  });
  const withdraw = useMutation({
    mutationFn: () => api.post(`/proformas/${proformaId}/void`, {}),
    onSuccess: refresh,
  });
  const convert = useMutation({
    mutationFn: () =>
      api.post<{ invoice: { id: string } }>(`/proformas/${proformaId}/convert`, {
        date: invoiceDate,
        markAsSent: true,
      }),
    onSuccess: (result) => {
      refresh();
      // NOW there is a receivable, and the tiles that count one are stale.
      void queryClient.invalidateQueries({ queryKey: ['invoices', org?.id] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard', org?.id] });
      navigate(`/invoices/${result.invoice.id}`);
    },
  });

  if (query.isLoading) {
    return (
      <>
        <PageHeader title="Proforma" />
        <div className="px-5">
          <Card>
            <SkeletonTable rows={5} cols={4} />
          </Card>
        </div>
      </>
    );
  }

  if (query.isError || !query.data) {
    return (
      <>
        <PageHeader title="Proforma" />
        <div className="px-5">
          <Card>
            <ErrorState error={query.error} onRetry={() => query.refetch()} />
          </Card>
        </div>
      </>
    );
  }

  const proforma = query.data;
  const failure = [send.error, withdraw.error, convert.error].find((e) => e instanceof ApiError) as
    | ApiError
    | undefined;
  const convertible = canConvert(proforma.status) && perms.can('proforma', 'manage');
  const sendable =
    perms.can('proforma', 'edit') &&
    canConvert(proforma.status) &&
    (proforma.status === 'draft' || proforma.revision.hasUnsentChanges);

  return (
    <>
      {/* The page's own chrome is not part of the document: nothing above
          the document section prints. */}
      <div className="print:hidden">
        <PageHeader
          title={proforma.number}
          subtitle={proforma.customer?.displayName}
          actions={
            <>
              <Button variant="ghost" size="sm" onClick={() => navigate('/proformas')}>
                <ArrowLeft className="size-4" aria-hidden />
                All proformas
              </Button>
              {/* Sending an already-sent quote is how Rev 2 comes into being.
                  The button is offered whenever there is something the customer
                  has not seen - which on a sent proforma is the ONLY way the
                  edit ever reaches them. */}
              {sendable ? (
                <Button variant="secondary" size="sm" loading={send.isPending} onClick={() => send.mutate()}>
                  <Send className="size-4" aria-hidden />
                  {proforma.revision.latest ? `Send revision ${proforma.revision.latest + 1}` : 'Send'}
                </Button>
              ) : null}
              {convertible && proforma.status !== 'draft' ? (
                <Button variant="outline" size="sm" loading={withdraw.isPending} onClick={() => withdraw.mutate()}>
                  <Ban className="size-4" aria-hidden />
                  Withdraw
                </Button>
              ) : null}
            </>
          }
        />
      </div>

      <div className="space-y-4 px-4 pb-10 sm:px-5 print:p-0">
        {failure ? (
          <p role="alert" className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive print:hidden">
            {failure.message}
          </p>
        ) : null}

        {/* The one thing this screen could not say before revisions existed.
            A sent proforma is editable, so the page and the PDF in the
            customer's inbox drift apart silently - and the person about to
            convert it had no way to know which figures the customer agreed
            to. */}
        {proforma.revision.hasUnsentChanges ? (
          <div
            role="note"
            className="flex flex-wrap items-start gap-2 rounded-md border border-warning/40 bg-warning/10 px-3 py-2.5 text-sm print:hidden"
          >
            <Info className="mt-0.5 size-4 shrink-0 text-warning" aria-hidden />
            <span>
              <strong className="font-semibold">Edited since it was sent.</strong> Your customer is still holding
              revision {proforma.revision.latest}
              {proforma.revision.latestTotal ? (
                <>
                  {' '}
                  at <Money value={proforma.revision.latestTotal} currency={proforma.currency} />
                </>
              ) : null}
              . Send it again to make these changes theirs
              {convertible ? ', or convert it and the invoice will carry what is on this screen' : ''}.
            </span>
          </div>
        ) : null}

        {proforma.convertedToInvoiceId ? (
          <div className="flex flex-wrap items-center gap-2 rounded-md border border-success/30 bg-success/10 px-3 py-2.5 text-sm print:hidden">
            <Info className="size-4 shrink-0 text-success" aria-hidden />
            <span>
              This became invoice{' '}
              <Link to={`/invoices/${proforma.convertedToInvoiceId}`} className="font-medium text-primary hover:underline">
                {proforma.convertedToInvoiceNumber}
              </Link>
              . That invoice is the live document now — this one is kept as the record of what was quoted.
            </span>
          </div>
        ) : (
          /* ALWAYS, on an unconverted proforma. See the header. */
          <div
            role="note"
            className="flex flex-wrap items-start gap-2 rounded-md border border-warning/40 bg-warning/10 px-3 py-2.5 text-sm print:hidden"
          >
            <Info className="mt-0.5 size-4 shrink-0 text-warning" aria-hidden />
            <span>
              <strong className="font-semibold">This is not a tax invoice.</strong> It shows the tax your customer will
              pay, but no supply has happened yet — so it creates no GST liability, appears on no return, and cannot be
              paid. Convert it to an invoice once the customer agrees.
            </span>
          </div>
        )}

        <div className="grid gap-4 print:hidden sm:grid-cols-2 lg:grid-cols-4">
          <Fact label="Status" value={<ProformaStatusBadge status={proforma.status} />} />
          <Fact label="Proforma date" value={formatDate(proforma.date)} />
          <Fact label="Valid until" value={formatDate(proforma.expiryDate)} />
          {/* "Amount quoted", never "Balance due". Nothing is due. */}
          <Fact
            label="Amount quoted"
            value={<Money value={proforma.total} currency={proforma.currency} className="text-base font-semibold" />}
          />
        </div>

        {convertible ? (
          <Card className="print:hidden">
            <CardContent className="flex flex-wrap items-end gap-4 pt-5">
              <div className="min-w-[180px]">
                <Input
                  label="Invoice date"
                  type="date"
                  value={invoiceDate}
                  onChange={(e) => setPickedInvoiceDate(e.target.value)}
                  // Not the proforma's date. The tax point is when the supply
                  // happens, and carrying the quote's date across would
                  // routinely backdate an invoice into a filed month.
                  hint="The date of supply. The invoice is priced at the rates in force then."
                />
              </div>
              <Button size="sm" loading={convert.isPending} onClick={() => convert.mutate()}>
                Convert to invoice
                <ArrowRight className="size-4" aria-hidden />
              </Button>
              <p className="text-xs text-muted-foreground">
                {proforma.status === 'expired'
                  ? 'This proforma has passed its validity date. Converting it is still fine if the customer has agreed.'
                  : 'This raises a real tax invoice, numbered in the invoice series, and the customer will owe it.'}
              </p>
            </CardContent>
          </Card>
        ) : null}

        <DocumentSection path="proformas" id={proforma.id} />

        <div className="space-y-4 print:hidden">
          <RevisionHistory proformaId={proforma.id} currency={proforma.currency} />

          <Attachments
            ownerType="proforma_invoice"
            ownerId={proforma.id}
            emptyHint="Signed copies, delivery proof, a customer purchase order. Drag one here or paste a screenshot."
          />
        </div>

        {proforma.customer ? (
          <p className="text-xs text-muted-foreground print:hidden">
            Quoted to{' '}
            <Link to="/customers" className="text-primary hover:underline">
              {proforma.customer.displayName}
            </Link>
            {proforma.customer.gstin ? ` · GSTIN ${proforma.customer.gstin}` : ''}
            {proforma.gstSplitKind ? ` · ${proforma.gstSplitKind.replace('_', '-')} supply` : ''}
          </p>
        ) : null}
      </div>
    </>
  );
}

/**
 * What was quoted, and when - one entry per revision the customer received.
 *
 * ── WHY EDITS ARE NOT IN HERE ──
 * A revision is minted by SENDING, so this list is the customer's side of the
 * conversation and nothing else. Saving the working copy nine times on a
 * Tuesday adds nothing to it, which is the whole reason it stays readable: the
 * question people open this for is "what did they agree to", and every row that
 * never left the building is noise against that.
 *
 * Rendered from the snapshot stored with each revision, never re-priced. A list
 * that recomputed old totals from today's catalogue would answer the question
 * it was asked with today's numbers.
 */
function RevisionHistory({ proformaId, currency }: { proformaId: string; currency: string }) {
  const query = useQuery({
    queryKey: ['proforma-revisions', proformaId],
    queryFn: () =>
      api.get<{
        items: ProformaRevision[];
        revisionsStartedAt: string | null;
        hasUnsentChanges: boolean;
      }>(`/proformas/${proformaId}/revisions`),
  });

  // A quote nobody has sent yet has no history, and an empty card asking to be
  // read is worse than no card.
  if (query.isLoading || query.isError || !query.data?.items.length) return null;

  return (
    <Card>
      <CardContent className="pt-5">
        <div className="flex items-center gap-2">
          <History className="size-4 text-muted-foreground" aria-hidden />
          <h2 className="text-sm font-medium">What was quoted</h2>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">
          Each time this quote was sent. Edits that were never sent are not here — the customer never saw them.
        </p>

        <ol className="mt-3 space-y-2">
          {query.data.items.map((entry) => (
            <li key={entry.id} className="rounded-md border border-border px-3 py-2">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2 text-sm font-medium">
                    <span>Revision {entry.revision}</span>
                    {entry.isFinal ? <Badge variant="info">Final — became the invoice</Badge> : null}
                    {/* Minted at conversion rather than by a send, which means
                        these figures were billed without being re-quoted. Worth
                        saying: it is the one row here the customer never got. */}
                    {entry.reason === 'converted' ? <Badge>Not sent — recorded at conversion</Badge> : null}
                  </div>
                  <div className="mt-0.5 text-xs text-muted-foreground">
                    {formatDateTime(entry.sentAt)}
                    {entry.sentByName ? ` · ${entry.sentByName}` : ''} · {entry.lineCount}{' '}
                    {entry.lineCount === 1 ? 'line' : 'lines'}
                  </div>
                  {entry.changes?.length ? (
                    <ul className="mt-1 space-y-0.5 text-xs text-muted-foreground">
                      {entry.changes.map((change) => (
                        <li key={change}>· {change}</li>
                      ))}
                    </ul>
                  ) : null}
                </div>
                <div className="tabular shrink-0 text-right text-sm font-medium">
                  <Money value={entry.total} currency={entry.currency || currency} />
                </div>
              </div>
            </li>
          ))}
        </ol>
      </CardContent>
    </Card>
  );
}

function Fact({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Card>
      <CardContent className="pt-4">
        <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
        <div className="mt-1.5 text-sm">{value}</div>
      </CardContent>
    </Card>
  );
}
